import type { CommonData } from "../../../../types/common";
import type { TinkAccount, TinkIncome } from "../../../../types/tink";
import { NormalInfoWrapper } from "../../../tasks/styles";
import { ModalLink } from "../../modalLink";
import { RowsWrapper } from "../../styles";

interface TinkDataRowProps {
  data: CommonData;
  onViewTransactions?: (accountId: string, applicantType: "applicant" | "coapplicant") => void;
}

interface Detail {
  label: string;
  value: string | React.ReactNode | null;
  isHeader?: boolean;
}

interface AgentTinkData {
  accounts?: TinkAccount[];
  income?: TinkIncome;
}

const parseAgentPsd2Data = (psd2DataString: string | undefined): AgentTinkData | null => {
  if (!psd2DataString) return null;
  try {
    return JSON.parse(psd2DataString) as AgentTinkData;
  } catch {
    return null;
  }
};

const getTransactionCountForAccount = (income: TinkIncome | undefined, accountId: string): number => {
  if (!income?.streams) return 0;
  return income.streams
    .filter(stream => stream.accountId === accountId)
    .reduce((total, stream) => total + (stream.transactions?.length || 0), 0);
};

const AccountsSection = ({
  accounts,
  income,
  title,
  applicantType,
  onViewTransactions,
}: {
  accounts: TinkAccount[];
  income: TinkIncome | undefined;
  title: string;
  applicantType: "applicant" | "coapplicant";
  onViewTransactions?: (accountId: string, applicantType: "applicant" | "coapplicant") => void;
}) => {
  if (!accounts || accounts.length === 0) return null;

  const accountDetails: Detail[] = accounts.flatMap((account, accountIndex) => {
    const details: Detail[] = [];

    if (accounts.length > 1) {
      details.push({ label: `Account ${accountIndex + 1}`, value: "", isHeader: true });
    }

    const holderName = account.users?.find(u => u.role === "HOLDER")?.name
      || account.holderNames?.join(", ")
      || null;

    details.push(
      { label: "Bank", value: account.financialInstitutionName || null },
      { label: "Account Number", value: account.accountNumber || null },
      { label: "Account Type", value: account.name || null },
      { label: "Account Holder", value: holderName },
      { label: "IBAN", value: account.iban || null },
    );

    const transactionCount = getTransactionCountForAccount(income, account.id);
    if (transactionCount > 0 && onViewTransactions) {
      details.push({
        label: "Transactions",
        value: (
          <ModalLink
            title={`View ${transactionCount} Transactions`}
            handleOnClick={() => onViewTransactions(account.id, applicantType)}
          />
        ),
      });
    }

    return details;
  });

  // Filter out details with null values (except headers)
  const filteredDetails = accountDetails.filter(
    detail => detail.isHeader || detail.value !== null
  );

  // Find the index of the next header or end of array for each item
  const isLastInSection = (index: number): boolean => {
    if (index === filteredDetails.length - 1) return true;
    return filteredDetails[index + 1]?.isHeader === true;
  };

  return (
    <RowsWrapper>
      <NormalInfoWrapper $header>{title}</NormalInfoWrapper>
      {filteredDetails.map((detail, index) => {
        if (detail.isHeader) {
          return (
            <NormalInfoWrapper
              key={`${title}-header-${detail.label}`}
              $header
              style={{ backgroundColor: "#f5f5f5", marginTop: index > 0 ? "8px" : "0" }}
            >
              {detail.label}
            </NormalInfoWrapper>
          );
        }

        return (
          <NormalInfoWrapper
            key={`${title}-${detail.label}-${index}`}
            $isLast={isLastInSection(index)}
          >
            <div>{detail.label}</div>
            <div>{detail.value}</div>
          </NormalInfoWrapper>
        );
      })}
    </RowsWrapper>
  );
};

export const TinkAccountsRow = ({ data, onViewTransactions }: TinkDataRowProps) => {
  // Postscore Tink data from integrations
  const tinkApplicant = data?.integrations?.tink?.applicant;
  const tinkCoapplicant = data?.integrations?.tink?.coapplicant;

  // Agent-submitted Tink data from psd2Data field
  const agentPsd2DataString = data?.agentApiApplication?.applicant?.psd2Data;
  const agentTinkData = parseAgentPsd2Data(agentPsd2DataString);

  // Check if agent data has streams (for transaction viewing)
  const agentIncomeWithStreams = agentTinkData?.income as TinkIncome | undefined;
  const hasAgentStreams = agentIncomeWithStreams?.streams && agentIncomeWithStreams.streams.length > 0;

  // Use postscore data if available, otherwise use agent data
  const applicantAccounts = tinkApplicant?.accounts || agentTinkData?.accounts || [];
  const coapplicantAccounts = tinkCoapplicant?.accounts || [];
  const applicantIncome: TinkIncome | undefined = tinkApplicant?.income || (hasAgentStreams ? agentIncomeWithStreams : undefined);
  const coapplicantIncome: TinkIncome | undefined = tinkCoapplicant?.income;

  const hasApplicantAccounts = applicantAccounts.length > 0;
  const hasCoapplicantAccounts = coapplicantAccounts.length > 0;

  if (!hasApplicantAccounts && !hasCoapplicantAccounts) {
    return (
      <RowsWrapper>
        <NormalInfoWrapper $isLast>No Tink data available</NormalInfoWrapper>
      </RowsWrapper>
    );
  }

  return (
    <>
      {hasApplicantAccounts && (
        <AccountsSection
          accounts={applicantAccounts}
          income={applicantIncome}
          title="Applicant Accounts"
          applicantType="applicant"
          onViewTransactions={onViewTransactions}
        />
      )}
      {hasCoapplicantAccounts && (
        <AccountsSection
          accounts={coapplicantAccounts}
          income={coapplicantIncome}
          title="Co-applicant Accounts"
          applicantType="coapplicant"
          onViewTransactions={onViewTransactions}
        />
      )}
    </>
  );
};
